import React from "react";
import type { DepGraphActivity, ExistingWorkCode } from "../../../../../models";
import WorkCodeField from "../../../../shared/WorkCodeField/WorkCodeField";
import { swatchHex } from "../../../../../utils/color";
import { formatDateMonthDay } from "../../../../../utils/format";
import { rowFinish, rowStart } from "../../graphRows";
import "../InlineAddRow/InlineAddRow.css";

const OUTSIDE_IGNORE =
  ".work-code-field-pop, .dep-typeahead, .dep-chip-popover";

export interface ApplyEditArgs {
  code: string;
  codeName: string;
  codeColor: string;
  name: string;
  days: number;
}

interface Props {
  activity: DepGraphActivity;
  scheduleId: number;
  extraCodes: ExistingWorkCode[];
  canSelect: boolean;
  /** The row's normal Predecessors / Successors cells — tie edits stay live
   *  through the standard chips while the fields are being edited. */
  predCell: React.ReactNode;
  succCell: React.ReactNode;
  onApply: (args: ApplyEditArgs) => void;
  onCancel: () => void;
  /** Editing session signal (the host's forward-pass hold). */
  onEditingChange?: (editing: boolean) => void;
  attachRowRef: (el: HTMLTableRowElement | null) => void;
}

/** Edit-in-place: the SAME editor as the add row, prepopulated from the
 *  activity — work code chip · name · days under their columns. ✓ applies the
 *  changes and recalculates; ✕ discards them (tie edits made through the
 *  chips stay, as everywhere else). */
const EditActivityRow: React.FC<Props> = ({
  activity,
  scheduleId,
  extraCodes,
  canSelect,
  predCell,
  succCell,
  onApply,
  onCancel,
  onEditingChange,
  attachRowRef,
}) => {
  const [wc, setWc] = React.useState({
    code: activity.work_code,
    name: activity.work_name || activity.work_code,
    color: activity.color,
  });
  const [name, setName] = React.useState(activity.name);
  const [days, setDays] = React.useState(String(activity.duration_days));
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  const editingChangeRef = React.useRef(onEditingChange);
  editingChangeRef.current = onEditingChange;
  React.useEffect(() => {
    editingChangeRef.current?.(true);
    return () => editingChangeRef.current?.(false);
  }, []);

  const daysNum = Math.max(1, Math.round(Number(days)) || 0);
  const canApply = name.trim().length > 0 && daysNum >= 1;

  const apply = () => {
    if (!canApply) return;
    onApply({
      code: wc.code.trim(),
      codeName: wc.name.trim() || wc.code.trim(),
      codeColor: wc.code ? swatchHex(wc.color, wc.code) : "",
      name: name.trim(),
      days: daysNum,
    });
  };

  const keyHandler = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") apply();
    else if (e.key === "Escape") onCancel();
  };

  const rowElRef = React.useRef<HTMLTableRowElement | null>(null);
  const cancelRef = React.useRef(onCancel);
  cancelRef.current = onCancel;
  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (rowElRef.current?.contains(t)) return;
      if (t.closest(OUTSIDE_IGNORE)) return;
      cancelRef.current();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const setRowRef = (el: HTMLTableRowElement | null) => {
    rowElRef.current = el;
    attachRowRef(el);
  };

  return (
    <tr ref={setRowRef} className="dep-table-ghost-row dep-table-ghost-row--editing">
      {canSelect && <td className="dep-table-col-select" />}
      <td className="dep-table-col-name">
        <div className="dep-table-ghost-edit">
          <WorkCodeField
            scheduleId={scheduleId}
            code={wc.code}
            name={wc.name}
            color={wc.color}
            onChange={(p) =>
              setWc((prev) => ({
                code: p.code !== undefined ? p.code : prev.code,
                name: p.name !== undefined ? p.name : prev.name,
                color: p.color !== undefined ? p.color : prev.color,
              }))
            }
            extraCodes={extraCodes}
          />
          <input
            ref={nameInputRef}
            className="dep-table-ghost-name"
            autoFocus
            title={name}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={keyHandler}
          />
        </div>
      </td>
      <td className="dep-table-col-date">
        <span className="dep-table-date">
          {rowStart(activity) ? formatDateMonthDay(rowStart(activity)!) : "—"}
        </span>
      </td>
      <td className="dep-table-col-date">
        <span className="dep-table-date">
          {rowFinish(activity) ? formatDateMonthDay(rowFinish(activity)!) : "—"}
        </span>
      </td>
      <td className="dep-table-col-dur">
        <input
          className="dep-table-ghost-days"
          type="number"
          min={1}
          value={days}
          title="Duration (workdays)"
          onChange={(e) => setDays(e.target.value)}
          onKeyDown={keyHandler}
        />
      </td>
      <td className="dep-table-col-chips">{predCell}</td>
      <td className="dep-table-col-chips">{succCell}</td>
      <td className="dep-table-col-actions">
        <div className="dep-table-ghost-actions">
          <button
            type="button"
            className="dep-table-ghost-commit"
            disabled={!canApply}
            title="Apply the changes and recalculate the schedule (Enter)"
            onClick={apply}
          >
            ✓
          </button>
          <button
            type="button"
            className="dep-table-ghost-cancel"
            title="Discard the changes (Esc)"
            onClick={onCancel}
          >
            ✕
          </button>
        </div>
      </td>
    </tr>
  );
};

export default EditActivityRow;
